import { useMemo, useState } from 'react';
import { Copy, Image as ImageIcon, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ASSET_REGISTRIES, type AssetEntry, type AssetSource } from '../lib/asset-registry';

const SOURCES: AssetSource[] = [
  'bg',
  'enemy',
  'character',
  'icons',
  'images',
  'effects',
  'objects',
  'audio',
];

interface FlatEntry {
  key: string; // unique within source (may include parent path for nested registries)
  displayKey: string; // last segment for display
  entry: AssetEntry;
}

/** Flattens a registry, walking depth-1 nested namespaces (e.g., icons.currency.emerald). */
function flattenRegistry(reg: Record<string, unknown>): FlatEntry[] {
  const out: FlatEntry[] = [];
  for (const [k, v] of Object.entries(reg)) {
    if (v && typeof v === 'object' && 'url' in (v as Record<string, unknown>)) {
      out.push({ key: k, displayKey: k, entry: v as AssetEntry });
    } else if (v && typeof v === 'object') {
      for (const [nestedK, nestedV] of Object.entries(v as Record<string, unknown>)) {
        if (
          nestedV &&
          typeof nestedV === 'object' &&
          'url' in (nestedV as Record<string, unknown>)
        ) {
          out.push({
            key: `${k}.${nestedK}`,
            displayKey: nestedK,
            entry: nestedV as AssetEntry,
          });
        }
      }
    }
  }
  return out;
}

export function AssetViewerPage() {
  const [activeSource, setActiveSource] = useState<AssetSource>('bg');
  const [search, setSearch] = useState('');

  const flatBySource = useMemo(() => {
    const result: Record<AssetSource, FlatEntry[]> = {} as Record<AssetSource, FlatEntry[]>;
    for (const s of SOURCES) {
      result[s] = flattenRegistry(ASSET_REGISTRIES[s]);
    }
    return result;
  }, []);

  const filtered = useMemo(() => {
    const all = flatBySource[activeSource] ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (f) =>
        f.key.toLowerCase().includes(needle) ||
        (f.entry.description ?? '').toLowerCase().includes(needle)
    );
  }, [flatBySource, activeSource, search]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Asset Viewer</h1>
        <span className="text-xs text-muted-foreground">
          read-only — bundled from <code>apps/client/src/assets/*.json</code>
        </span>
      </div>

      <Tabs value={activeSource} onValueChange={(v) => setActiveSource(v as AssetSource)}>
        <div className="flex items-center gap-3">
          <TabsList>
            {SOURCES.map((s) => (
              <TabsTrigger key={s} value={s}>
                {s} <span className="ml-1 text-xs opacity-60">({flatBySource[s].length})</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <Input
            type="search"
            placeholder="filter by key or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {SOURCES.map((s) => (
          <TabsContent key={s} value={s} className="mt-3">
            {s === activeSource && <AssetGrid entries={filtered} source={activeSource} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function AssetGrid({ entries, source }: { entries: FlatEntry[]; source: AssetSource }) {
  if (entries.length === 0) {
    return <div className="text-sm text-muted-foreground">No matching assets.</div>;
  }
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
      {entries.map((f) => (
        <AssetCard key={f.key} flat={f} source={source} />
      ))}
    </div>
  );
}

function AssetCard({ flat, source }: { flat: FlatEntry; source: AssetSource }) {
  const { entry, key, displayKey } = flat;
  const isAudio = source === 'audio';

  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-2 p-2 text-xs">
        <div className="aspect-square w-full overflow-hidden rounded-md bg-muted/30 flex items-center justify-center">
          {isAudio ? (
            <div className="flex flex-col items-center gap-2">
              <Volume2 className="h-8 w-8 text-muted-foreground" />
              <audio controls src={entry.url} className="max-w-full">
                <track kind="captions" />
              </audio>
            </div>
          ) : entry.url ? (
            <img
              src={entry.url}
              alt={key}
              loading="lazy"
              className="h-full w-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div className="font-mono text-[11px] break-all" title={key}>
          {displayKey}
          {key !== displayKey && <span className="ml-1 text-muted-foreground">({key})</span>}
        </div>
        {entry.description && (
          <div className="text-muted-foreground line-clamp-2" title={entry.description}>
            {entry.description}
          </div>
        )}
        {entry.metadata?.width && entry.metadata?.height && (
          <div className="text-muted-foreground">
            {entry.metadata.width} × {entry.metadata.height}
          </div>
        )}
        <div className="flex items-center gap-1">
          <code className="flex-1 truncate text-[10px] text-muted-foreground" title={entry.url}>
            {entry.url}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => navigator.clipboard.writeText(entry.url)}
            aria-label="Copy URL"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default AssetViewerPage;
